import { Checkbox, Fieldset, Group, Input, Stack, TextInput } from "@mantine/core";
import { useEffect } from "react";
import { useIpawsAlertFormContext } from "shared/components/ipaws/alert/context";
import { IpawsAlertFormProfileCertificateSelect } from "shared/components/ipaws/alert/IpawsAlertFormProfileCertificateSelect";
import { IpawsAlertFormStepLayout } from "shared/components/ipaws/alert/IpawsAlertFormStepLayout";
import {
    useIpawsAlertFormAllowedEventCodes,
    useIpawsAlertFormAllowedGeoCodes,
    useIpawsAlertFormCertificate,
} from "shared/components/ipaws/alert/query";
import { formatIpawsChannel } from "shared/components/ipaws/format";
import { useFormState } from "shared/hooks/useFormState";
import { arr } from "shared/utils/array";
import { to } from "shared/utils/fns";
import { IpawsChannel } from "src/gql/graphql";

export const IpawsAlertFormProfile = (props: { widget?: boolean }) => {
    const form = useIpawsAlertFormContext();
    const [channels, setChannels] = useFormState(form, "channels");

    const [, setAllowedEventCodes] = useFormState(form, "meta.allowedEventCodes");
    const [, setAllowedGeocodes] = useFormState(form, "meta.allowedGeocodes");

    const { data: cert } = useIpawsAlertFormCertificate();
    const { data: allowedEventCodes } = useIpawsAlertFormAllowedEventCodes();
    const { data: allowedGeoCodes } = useIpawsAlertFormAllowedGeoCodes();

    useEffect(() => {
        setAllowedEventCodes(allowedEventCodes?.map(to("id")) ?? []);
    }, [allowedEventCodes]);

    useEffect(() => {
        setAllowedGeocodes(allowedGeoCodes?.map(to("code")) ?? []);
    }, [allowedGeoCodes]);

    useEffect(() => {
        if (!cert) return;
        if (!arr(channels).except(...(cert.ipawsCertificate.channels.filter(to("userEnabled")).map(to("channel")) ?? [])).isEmpty) {
            setChannels([]);
        }
    }, [cert?.ipawsCertificate.id]);

    return (
        <IpawsAlertFormStepLayout>
            <Stack>
                <IpawsAlertFormProfileCertificateSelect {...props} />
                <Fieldset variant="unstyled" disabled={!cert}>
                    <Stack>
                        <Input.Wrapper error={form.getInputProps("channels").error}>
                            <Stack gap="xs">
                                <Input.Label>Channels</Input.Label>
                                <Group>
                                    <IpawsAlertFormProfileChannelCheckbox channel={IpawsChannel.Eas} widget={props.widget} />
                                    <IpawsAlertFormProfileChannelCheckbox channel={IpawsChannel.Cmas} widget={props.widget} />
                                    <IpawsAlertFormProfileChannelCheckbox channel={IpawsChannel.Nwem} widget={props.widget} />
                                    <IpawsAlertFormProfileChannelCheckbox channel={IpawsChannel.Capexch} widget={props.widget} />

                                    <Checkbox checked disabled label={formatIpawsChannel(IpawsChannel.Public)} />
                                </Group>
                                <Input.Error />
                            </Stack>
                        </Input.Wrapper>

                        {channels.includes(IpawsChannel.Capexch) && <TextInput label="Note" {...form.getInputProps("note")} />}
                    </Stack>
                </Fieldset>
            </Stack>
        </IpawsAlertFormStepLayout>
    );
};

const IpawsAlertFormProfileChannelCheckbox = (props: { channel: IpawsChannel; widget?: boolean }) => {
    const form = useIpawsAlertFormContext();
    const [channels, setChannels] = useFormState(form, "channels");
    const [mode] = useFormState(form, "meta.mode");
    const cert = useIpawsAlertFormCertificate();

    const isVisible = !cert.data ? true : cert.data.ipawsCertificate.channels.some((ch) => ch.channel == props.channel && ch.userEnabled);
    const isUpdate = typeof mode !== "string" && mode.type === "update";
    const isInitialSelection = form.getInitialValues().channels.includes(props.channel);

    return (
        <Checkbox
            label={formatIpawsChannel(props.channel)}
            checked={!cert.data ? false : channels.includes(props.channel)}
            onChange={(e) =>
                setChannels(e.target.checked ? arr(channels).append(props.channel).take() : arr(channels).except(props.channel).take())
            }
            style={{ display: isVisible ? "unset" : "none" }}
            disabled={props.widget || (isUpdate && !isInitialSelection)}
        />
    );
};
